import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CubeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';

interface FractionalNFT {
  id: string;
  title: string;
  image: string;
  collection: string;
  totalSupply: number;
  pricePerFraction: number;
  originalPrice: number;
  currentValue: number;
  owners: number;
  yourOwnership: number;
  isLocked: boolean;
  buyoutPrice?: number;
  dao: {
    proposals: number;
    members: number;
  };
}

const mockFractionalNFTs: FractionalNFT[] = [
  {
    id: '1',
    title: 'Cosmic Voyager #1',
    image: 'https://picsum.photos/seed/frac1/400/400',
    collection: 'Cosmic Collection',
    totalSupply: 10000,
    pricePerFraction: 0.015,
    originalPrice: 125,
    currentValue: 150,
    owners: 847,
    yourOwnership: 500,
    isLocked: false,
    dao: { proposals: 12, members: 523 },
  },
  {
    id: '2',
    title: 'Digital Dreams Genesis',
    image: 'https://picsum.photos/seed/frac2/400/400',
    collection: 'Dream Series',
    totalSupply: 5000,
    pricePerFraction: 0.025,
    originalPrice: 100,
    currentValue: 125,
    owners: 342,
    yourOwnership: 0,
    isLocked: false,
    dao: { proposals: 8, members: 312 },
  },
  {
    id: '3',
    title: 'Mythical Phoenix',
    image: 'https://picsum.photos/seed/frac3/400/400',
    collection: 'Mythical Beasts',
    totalSupply: 10000,
    pricePerFraction: 0.045,
    originalPrice: 350,
    currentValue: 450,
    owners: 1234,
    yourOwnership: 1000,
    isLocked: true,
    buyoutPrice: 500,
    dao: { proposals: 24, members: 987 },
  },
  {
    id: '4',
    title: 'Abstract Mind #1',
    image: 'https://picsum.photos/seed/frac4/400/400',
    collection: 'Abstract Art',
    totalSupply: 8000,
    pricePerFraction: 0.02,
    originalPrice: 140,
    currentValue: 160,
    owners: 567,
    yourOwnership: 200,
    isLocked: false,
    dao: { proposals: 6, members: 445 },
  },
];

const FractionalPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browse' | 'owned' | 'create'>('browse');
  const [selectedNFT, setSelectedNFT] = useState<FractionalNFT | null>(null);
  const [buyAmount, setBuyAmount] = useState('');

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const userStats = {
    totalValue: 32.5,
    totalFractions: 1700,
    uniqueNFTs: 3,
    votingPower: 1700,
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Fractional Ownership 2.0</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Own a piece of expensive NFTs, participate in DAO governance, and trade fractions freely.
            High-value NFTs are automatically fractionalized after auction.
          </p>
        </motion.div>

        {/* User Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="glass-card p-5">
            <div className="flex items-center space-x-2 mb-2">
              <CurrencyDollarIcon className="h-5 w-5 text-primary-400" />
              <span className="text-sm text-gray-400">Total Value</span>
            </div>
            <p className="text-2xl font-bold">{userStats.totalValue} ETH</p>
          </div>
          <div className="glass-card p-5">
            <div className="flex items-center space-x-2 mb-2">
              <CubeIcon className="h-5 w-5 text-green-400" />
              <span className="text-sm text-gray-400">Total Fractions</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(userStats.totalFractions)}</p>
          </div>
          <div className="glass-card p-5">
            <div className="flex items-center space-x-2 mb-2">
              <SparklesIcon className="h-5 w-5 text-purple-400" />
              <span className="text-sm text-gray-400">Unique NFTs</span>
            </div>
            <p className="text-2xl font-bold">{userStats.uniqueNFTs}</p>
          </div>
          <div className="glass-card p-5">
            <div className="flex items-center space-x-2 mb-2">
              <ShieldCheckIcon className="h-5 w-5 text-amber-400" />
              <span className="text-sm text-gray-400">Voting Power</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(userStats.votingPower)}</p>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold mb-6">How Fractional Ownership Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              {
                icon: CubeIcon,
                title: 'Auto-Fractionalize',
                description: 'NFTs sold above 10 ETH are automatically split into 10,000 ERC-20 fractions',
                color: 'text-primary-400',
                bgColor: 'bg-primary-500/10',
              },
              {
                icon: UserGroupIcon,
                title: 'DAO Governance',
                description: 'Each fraction = 1 vote. Participate in decisions about the NFT',
                color: 'text-green-400',
                bgColor: 'bg-green-500/10',
              },
              {
                icon: ArrowTrendingUpIcon,
                title: 'Trade Freely',
                description: 'Buy and sell fractions on the open market. Price floats with demand',
                color: 'text-amber-400',
                bgColor: 'bg-amber-500/10',
              },
              {
                icon: LockClosedIcon,
                title: 'Buyout Mechanism',
                description: 'Anyone with >10% can initiate a buyout. DAO votes to accept or reject',
                color: 'text-purple-400',
                bgColor: 'bg-purple-500/10',
              },
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="glass-card p-6 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${step.bgColor} flex items-center justify-center`}>
                    <Icon className={`h-8 w-8 ${step.color}`} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-gray-400 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-1 border-b border-white/10">
            {[
              { id: 'browse', label: 'Browse All' },
              { id: 'owned', label: 'Your Holdings' },
              { id: 'create', label: 'Create Fractional' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-4 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="fractionalActiveTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500"
                  />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Fractional NFTs Grid */}
        {activeTab !== 'create' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {mockFractionalNFTs
              .filter((nft) => activeTab === 'browse' || nft.yourOwnership > 0)
              .map((nft) => (
                <div
                  key={nft.id}
                  className="glass-card overflow-hidden hover:border-primary-500/50 transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative aspect-square">
                    <img
                      src={nft.image}
                      alt={nft.title}
                      className="w-full h-full object-cover"
                    />
                    {nft.isLocked && (
                      <div className="absolute top-3 right-3 flex items-center space-x-1 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 backdrop-blur-sm">
                        <LockClosedIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">Buyout Active</span>
                      </div>
                    )}
                    {nft.yourOwnership > 0 && (
                      <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-400 backdrop-blur-sm">
                        <span className="text-xs font-medium">
                          You own {((nft.yourOwnership / nft.totalSupply) * 100).toFixed(2)}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <p className="text-xs text-gray-500 mb-1">{nft.collection}</p>
                    <h3 className="font-semibold text-lg mb-3">{nft.title}</h3>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Price/Fraction</p>
                        <p className="font-semibold">{nft.pricePerFraction} ETH</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Total Value</p>
                        <p className="font-semibold">{nft.currentValue} ETH</p>
                        <p className={`text-xs ${nft.currentValue > nft.originalPrice ? 'text-green-400' : 'text-red-400'}`}>
                          {nft.currentValue > nft.originalPrice ? '+' : ''}
                          {(((nft.currentValue - nft.originalPrice) / nft.originalPrice) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-400 mb-4 pb-4 border-b border-white/5">
                      <div className="flex items-center space-x-1">
                        <UserGroupIcon className="h-4 w-4" />
                        <span>{formatNumber(nft.owners)} owners</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CubeIcon className="h-4 w-4" />
                        <span>{formatNumber(nft.totalSupply)} fractions</span>
                      </div>
                    </div>

                    {/* DAO Info */}
                    <div className="flex items-center justify-between text-sm mb-4">
                      <div className="flex items-center space-x-1 text-purple-400">
                        <BuildingLibraryIcon className="h-4 w-4" />
                        <span>{nft.dao.proposals} proposals</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-400">
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>{formatNumber(nft.dao.members)} voters</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedNFT(nft)}
                        className="flex-1 btn-primary"
                      >
                        Buy Fractions
                      </button>
                      {nft.yourOwnership > 0 && (
                        <button className="px-4 py-2 bg-dark-700 border border-white/10 rounded-lg hover:border-white/20 transition-colors">
                          Sell
                        </button>
                      )}
                    </div>

                    {nft.isLocked && nft.buyoutPrice && (
                      <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <div className="flex items-center space-x-2 text-amber-400 text-sm">
                          <InformationCircleIcon className="h-4 w-4" />
                          <span>Buyout offer: {nft.buyoutPrice} ETH</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </motion.div>
        )}

        {/* Create Fractional Tab */}
        {activeTab === 'create' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-2xl mx-auto"
          >
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold mb-6">Fractionalize Your NFT</h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Select NFT
                  </label>
                  <select className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500">
                    <option value="">Choose an NFT from your wallet</option>
                    <option value="1">Cosmic Voyager #123</option>
                    <option value="2">Digital Dreams #456</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Total Supply of Fractions
                  </label>
                  <input
                    type="number"
                    placeholder="10000"
                    className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 10,000 for easier trading
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Initial Price per Fraction
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="0.01"
                      step="0.001"
                      className="w-full px-4 py-3 pr-16 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
                    />
                    <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                      ETH
                    </span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-primary-500/10 border border-primary-500/20">
                  <div className="flex items-start space-x-3">
                    <InformationCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-300">
                      <p className="mb-2">
                        When you fractionalize your NFT:
                      </p>
                      <ul className="space-y-1 text-gray-400">
                        <li>• The NFT will be locked in the vault contract</li>
                        <li>• You'll receive all ERC-20 fraction tokens</li>
                        <li>• A DAO will be created for governance</li>
                        <li>• Anyone with 10%+ can initiate a buyout</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button className="w-full btn-primary py-4">
                  Fractionalize NFT
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Buy Modal */}
        {selectedNFT && (
          <div className="fixed inset-0 bg-dark-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-6 max-w-md w-full"
            >
              <div className="flex items-center space-x-4 mb-6">
                <img
                  src={selectedNFT.image}
                  alt={selectedNFT.title}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div>
                  <h3 className="font-semibold text-lg">{selectedNFT.title}</h3>
                  <p className="text-sm text-gray-400">{selectedNFT.collection}</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Price per Fraction</span>
                  <span className="font-semibold">{selectedNFT.pricePerFraction} ETH</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Available</span>
                  <span className="font-semibold">
                    {formatNumber(selectedNFT.totalSupply - (selectedNFT.yourOwnership || 0))} fractions
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Amount to Buy
                </label>
                <input
                  type="number"
                  value={buyAmount}
                  onChange={(e) => setBuyAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
                />
                {buyAmount && (
                  <p className="text-sm text-gray-400 mt-2">
                    Total: {(parseFloat(buyAmount) * selectedNFT.pricePerFraction).toFixed(4)} ETH
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setSelectedNFT(null)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
                <button className="flex-1 btn-primary">
                  Buy Fractions
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FractionalPage;
